import { __awaiter, __generator } from "tslib";
var stateSymbol = Symbol("neo.debounce.helper");
var DebounceHelper = /** @class */ (function () {
    /**
     * Creates a debounce helper instance.
     * @param callback Callback function to run. All state passed to the execute method from current and previous calls will be passed to the callback.
     * @param delayMs Delay in milliseconds.
     */
    function DebounceHelper(callback, delayMs) {
        if (delayMs === void 0) { delayMs = 0; }
        this.callback = callback;
        this.delayMs = delayMs;
        this.state = [];
        this.executeCallback = this.executeCallback.bind(this);
    }
    /**
     * Debounce a function so that it is only called after a period of inactivity.
     * @param syncInstance The instance to synchronize against.
     *  This should always be the same object for the function you are debouncing.
     *  Do not debounce different functions on the same sync instance. Rather create a DebounceHelper instance for each function.
     * @param fn Function to call when the delay has elapsed.
     * @param delayMs Delay in milliseconds.
     * @param state State to pass to debounced method as an array of values for each time debounce was called.
     */
    DebounceHelper.debounce = function (syncInstance, callback, delayMs, state) {
        var helper = syncInstance[stateSymbol];
        if (!helper) {
            helper = new DebounceHelper(callback, delayMs);
            syncInstance[stateSymbol] = helper;
        }
        helper.callback = callback;
        helper.delayMs = delayMs;
        return helper.execute(state);
    };
    DebounceHelper.hasPending = function (syncInstance) {
        var helper = syncInstance[stateSymbol];
        if (helper) {
            return helper.hasPending;
        }
        else {
            return false;
        }
    };
    /** Cancels pending execution of the callback. Pending promise will be resolved with cancelled: true. */
    DebounceHelper.cancelPending = function (syncInstance) {
        var helper = syncInstance[stateSymbol];
        if (helper) {
            return helper.cancelPending();
        }
        else {
            return [];
        }
    };
    /** Fires the latest callback immediately. Pending promise from execute() will also be resolved with the return value of the callback. */
    DebounceHelper.executeImmediate = function (syncInstance) {
        var helper = syncInstance[stateSymbol];
        if (helper) {
            return helper.executeImmediate();
        }
        else {
            return Promise.resolve();
        }
    };
    Object.defineProperty(DebounceHelper.prototype, "hasPending", {
        /** Indicates that there is a pending execution. */
        get: function () {
            return this.currentResolver !== undefined;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Waits the interval defined in the constructor, then executes the callback.
     * The callback will not execute if execute is called again within the interval.
     * @param state State to pass to the callback.
     */
    DebounceHelper.prototype.execute = function (state) {
        var _this = this;
        this.cancelPending();
        if (state !== undefined)
            this.state.push(state);
        return new Promise(function (resolve, reject) {
            _this.currentResolver = resolve;
            _this.currentRejection = reject;
            _this.timer = setTimeout(_this.executeCallback, _this.delayMs);
        });
    };
    DebounceHelper.prototype.executeCallback = function () {
        return __awaiter(this, void 0, void 0, function () {
            var returnValue, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, 3, 4]);
                        return [4 /*yield*/, Promise.resolve(this.callback(this.state))];
                    case 1:
                        returnValue = _a.sent();
                        if (this.currentResolver) {
                            this.currentResolver({ cancelled: false, value: returnValue });
                        }
                        return [3 /*break*/, 4];
                    case 2:
                        e_1 = _a.sent();
                        if (this.currentRejection) {
                            this.currentRejection(e_1);
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        this.currentResolver = undefined;
                        this.currentRejection = undefined;
                        this.state = [];
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /** Cancels pending execution of the callback. Pending promise will be resolved with cancelled: true. */
    DebounceHelper.prototype.cancelPending = function () {
        if (this.timer)
            clearTimeout(this.timer);
        var state = this.state;
        if (this.currentResolver) {
            this.currentResolver({ cancelled: true });
            this.currentResolver = undefined;
            this.currentRejection = undefined;
            this.state = [];
        }
        return state;
    };
    /** Fires the latest callback immediately. Pending promise from execute() will also be resolved with the return value of the callback. */
    DebounceHelper.prototype.executeImmediate = function () {
        if (this.timer)
            clearTimeout(this.timer);
        return this.executeCallback();
    };
    return DebounceHelper;
}());
export { DebounceHelper };
